import { QueryCache, QueryClient, MutationCache } from '@tanstack/react-query';
import { ApiClientError } from './apiClient';

/** A stale/expired session leaves TanStack's cached `me` data in place on a
 *  failed refetch (v5 keeps prior data on error), so the auth guard keeps
 *  reading "logged in" while every other query 401s. Catch it globally here
 *  instead of threading a check through every call site: clear the cache and
 *  hard-redirect to /login so the next load starts from a clean slate. Guard
 *  against redirect loops for calls that 401 while already on /login (e.g.
 *  the login page's own auth check). */
function handleAuthError(error: unknown) {
  if (
    error instanceof ApiClientError &&
    error.status === 401 &&
    window.location.pathname !== '/login'
  ) {
    queryClient.clear();
    window.location.href = '/login';
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: handleAuthError }),
  mutationCache: new MutationCache({ onError: handleAuthError }),
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry auth/permission/not-found; retry transient errors up to twice.
        if (error instanceof ApiClientError && error.status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
    },
  },
});
